import { ref, onBeforeUnmount, getCurrentInstance } from "vue";

/**
 * Eine Funktion in festem Takt wiederholen — mit den vier Vorkehrungen, ohne
 * die ein Polling im Betrieb Schaden anrichtet.
 *
 * ⚠️ Bis zum 18.08. hatte dieses Modul KEINEN Importeur. Es lag fertig da und
 * wurde nie benutzt; die Nachrichtenliste lud genau einmal im `mounted()`.
 * Mit dem Warteschlangen-Indikator bekommt es seinen ersten Nutzer, und dabei
 * sind vier Dinge dazugekommen, die alle einen konkreten Anlass haben:
 *
 *   1. Das Intervall ist an `start()` übergebbar. Es kommt vom Dienst
 *      (`poll_sekunden`), nicht aus dieser Datei — schneller als der Worker
 *      pollt, kann nichts Neues erscheinen, und eine Zahl hier wäre die
 *      zweite Wahrheit neben `RATING_WORKER_POLL`.
 *   2. Pause bei `document.hidden`. Ein Tab im Hintergrund fragt sonst
 *      stundenlang nach einem Zustand, den niemand ansieht.
 *   3. Abbruch nach mehreren Fehlern in Folge. Ohne ihn läuft ein Tab mit
 *      abgelaufener Anmeldung endlos gegen 401 — genau das Verhalten, das der
 *      Chat-Kanal seit jeher zeigt (`Global SSE error: Event`).
 *   4. `onBeforeUnmount` wird nur registriert, wenn es eine aktive Instanz
 *      gibt. Options-API-Komponenten rufen dieses Composable außerhalb von
 *      `setup()`; dort ist der Hook wirkungslos und warnt nur. Wer es von dort
 *      benutzt, ruft `stop()` selbst in `beforeUnmount()`.
 *   5. `sofort: false` lässt den ersten Aufruf aus. Wer das Intervall aus
 *      der ersten Antwort holt, hat sie schon geholt, bevor er starten kann —
 *      ohne den Schalter stünde dieselbe Abfrage zweimal am Anfang.
 */
export function usePolling(fn, interval = 5000, { maxFehler = 3, sofort = true } = {}) {
    const timer = ref(null);
    const running = ref(false);
    const fehler = ref(0);
    let sichtbarkeit = null;

    const einmal = async () => {
        // Im Hintergrund gar nicht erst fragen. Kein clearInterval: der Takt
        // läuft weiter und greift von selbst wieder, sobald der Tab sichtbar
        // ist — sonst müsste das Wiederanlaufen eigens verdrahtet werden.
        if (typeof document !== "undefined" && document.hidden) return;
        try {
            await fn();
            fehler.value = 0;
        } catch (e) {
            fehler.value += 1;
            if (fehler.value >= maxFehler) {
                // Aufgeben ist hier richtig: die Ursache ist nichts, was sich
                // durch Wiederholen bessert (abgelaufenes Token, Dienst weg).
                // Ein Polling, das ewig weiterläuft, füllt nur die Konsole.
                stop();
            }
        }
    };

    const start = async (ms) => {
        if (running.value) return;
        running.value = true;
        fehler.value = 0;
        // `sofort: false` für Aufrufer, die den ersten Abruf schon selbst
        // gefahren haben — sonst steht am Anfang dieselbe Abfrage zweimal.
        if (sofort) await einmal();
        // ⚠️ Nach dem await noch einmal prüfen. Der erste Aufruf kann Sekunden
        // dauern, und in dieser Zeit kann `stop()` gelaufen sein — beim
        // Verlassen der Seite, oder aus `einmal()` selbst, wenn `maxFehler`
        // erreicht ist. Ohne diese Zeile überschrieb `start()` danach alles,
        // was `stop()` aufgeräumt hatte: der Takt lief weiter, und zwar an
        // einer Komponente, die es nicht mehr gibt.
        if (!running.value) return;
        timer.value = setInterval(einmal, ms || interval);

        // Beim Sichtbarwerden sofort nachfragen, statt bis zum nächsten Takt
        // zu warten — sonst steht die Liste nach dem Zurückwechseln noch bis
        // zu einem vollen Intervall veraltet da.
        if (typeof document !== "undefined") {
            sichtbarkeit = () => {
                if (!document.hidden && running.value) einmal();
            };
            document.addEventListener("visibilitychange", sichtbarkeit);
        }
    };

    const stop = () => {
        running.value = false;
        if (timer.value) {
            clearInterval(timer.value);
            timer.value = null;
        }
        if (sichtbarkeit && typeof document !== "undefined") {
            document.removeEventListener("visibilitychange", sichtbarkeit);
            sichtbarkeit = null;
        }
    };

    // Nur mit aktiver Instanz — s. Punkt 4 oben.
    if (getCurrentInstance()) onBeforeUnmount(stop);

    return { start, stop, running, fehler };
}
